import { Button, Callout, EmptyState, Icon, SectionHeader, Spinner, Stamp } from "../ui";
import { LABELS_16, MOCK_EXTRACTIONS } from "../data/mock";
import { useExtractions, useExtractionsSummary } from "../hooks/useExtractions";
import type { Extraction, LabelDef, Project } from "../types";

export interface Step5OverviewENProps {
  project: Project;
  mock: boolean;
  onAdvance: (n: number) => void;
}

function getExtractionText(extraction: Extraction): string {
  return extraction.text_edited_en || extraction.text_verbatim_en;
}

function shouldShowInOverview(extraction: Extraction): boolean {
  if (extraction.status === "rejected") return false;
  return extraction.confidence == null || extraction.confidence >= 0.65;
}

export function Step5OverviewEN({ project, mock, onAdvance }: Step5OverviewENProps) {
  const extractionsQuery = useExtractions(project.id, undefined, !mock);
  const summaryQuery = useExtractionsSummary(mock ? undefined : project.id);
  const extractions = mock ? MOCK_EXTRACTIONS : (extractionsQuery.data ?? []);
  const summaryByLabel = new Map(
    (summaryQuery.data ?? []).map((row) => [row.label, row]),
  );

  const grouped: Record<string, LabelDef[]> = {};
  for (const lab of LABELS_16) {
    if (!grouped[lab.group]) grouped[lab.group] = [];
    grouped[lab.group]!.push(lab);
  }
  const extByLabel: Record<string, Extraction[]> = {};
  for (const extraction of extractions) {
    if (!shouldShowInOverview(extraction)) {
      continue;
    }
    (extByLabel[extraction.label] = extByLabel[extraction.label] || []).push(extraction);
  }

  const isLoading = !mock && (extractionsQuery.isPending || summaryQuery.isPending);
  const hasError = !mock && (extractionsQuery.isError || summaryQuery.isError);
  const groups = Object.entries(grouped);

  return (
    <div className="anim-fade">
      <SectionHeader
        number={5}
        eyebrow="Etapa 05 · Pre-impressao"
        title="Visao geral · ingles"
        lede="Trechos extraidos agrupados na ordem do template Altox. Esta e a forma como a monografia se apresentara antes da revisao por trecho."
        action={
          <Button variant="ink" iconRight={Icon.ArrowRight} onClick={() => onAdvance(6)}>
            Ir para revisao
          </Button>
        }
      />

      <div className="border-y-2 border-[var(--ink)] py-10 mb-12 text-center">
        <div className="caption mb-3">Permitted Daily Exposure · Monograph Draft</div>
        <h1 className="display-1 leading-[0.94] mb-3">
          <em className="serif italic font-light">{project.drug_api_name}</em>
        </h1>
        <div className="caption">
          CAS {project.cas_number ?? "-"} · MW {project.molecular_weight ?? "-"}
        </div>
        <Stamp tone="strike" className="mt-6 inline-block rotate-[-3deg]">
          Draft · nao aprovado
        </Stamp>
      </div>

      {isLoading && (
        <div className="caption flex items-center gap-3 mb-8">
          <Spinner /> Carregando trechos extraidos...
        </div>
      )}

      {hasError && (
        <Callout tone="strike" icon={Icon.AlertCircle} title="Trechos indisponiveis">
          Nao foi possivel carregar os trechos extraidos. Recarregue a pagina ou tente novamente.
        </Callout>
      )}

      {!isLoading && !hasError && extractions.length === 0 && (
        <div className="mb-10">
          <EmptyState
            icon={Icon.FileText}
            title="Nenhum trecho extraido ainda"
            description="Volte para a etapa de extracao para gerar os trechos em ingles."
          />
        </div>
      )}

      {groups.map(([group, labels], gi) => (
        <section key={group} className="mb-14">
          <div className="flex items-baseline justify-between mb-2">
            <div className="eyebrow">Capitulo {String(gi + 1).padStart(2, "0")}</div>
            <div className="caption">
              {labels.length} {labels.length === 1 ? "secao" : "secoes"}
            </div>
          </div>
          <h2 className="display-3 mb-1">{group}</h2>
          <div className="hr-thick mb-8" />

          <div className="space-y-10">
            {labels.map((lab, li) => {
              const exts = extByLabel[lab.key] || [];
              const summary = summaryByLabel.get(lab.key);
              const approved = summary?.approved ?? exts.filter((e) => e.status === "approved").length;
              const pending = summary?.pending ?? exts.filter((e) => e.status === "pending").length;
              return (
                <article key={lab.key} className="grid grid-cols-12 gap-8">
                  <div className="col-span-12 md:col-span-3">
                    <div className="caption mb-1">§ {gi + 1}.{li + 1}</div>
                    <h3 className="display-5">{lab.title_en}</h3>
                    <div className="mono text-[11px] text-[var(--ink-3)] mt-1">
                      {lab.title_pt}
                    </div>
                    <div className="caption mt-2">
                      {exts.length} {exts.length === 1 ? "trecho" : "trechos"} · {approved} aprovados · {pending} pendentes
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-9">
                    {exts.length === 0 ? (
                      <div className="caption italic">- sem trechos extraidos -</div>
                    ) : (
                      <div className="space-y-4 dropcap-first">
                        {exts.map((e, ei) => {
                          const isFirstDropcap = gi === 0 && li === 0 && ei === 0;
                          return (
                            <p
                              key={e.id}
                              className={`serif whitespace-pre-wrap break-words text-[16px] leading-[1.65] text-[var(--ink)] ${isFirstDropcap ? "dropcap" : ""}`}
                            >
                              {getExtractionText(e)}
                              {e.citation_number && (
                                <sup className="mono text-[11px] text-[var(--ink-3)] ml-1 tabular">
                                  ({e.citation_number})
                                </sup>
                              )}
                            </p>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
